import React, { useState, useEffect, useRef } from 'react';
import { BRAND } from './product';
import { FilterState } from './types/filters';
import { filterCompaniesByState, filterCandidatesByState } from './utils/filterMatcher';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import AIReportModal, { ReportConfig } from './AIReportModal';
import ReportPreviewEditor from './ReportPreviewEditor';
import {
  KPICard,
  MetricCard,
  PremiumDoughnut,
  PremiumBarChart,
  BubbleChart,
  RankedList,
  ChartTypeSwitcher,
  SURFACE,
} from './charts';
import type { ChartType } from './charts';

interface AnalyticsProps {
  selectedProject: {
    id: number;
    name: string;
  };
  initialFilters?: FilterState | null;
  onNavigateToDataTable?: (filters: FilterState) => void;
}

interface Company {
  id: number;
  company_name: string;
  industry?: string;
  employee_count?: number;
  founded?: number;
  type?: string;
  funding_range?: string;
  business_type?: string[];
  business_stage?: string;
  annual_revenue?: string;
  [key: string]: any;
}

interface Candidate {
  id: number;
  name: string;
  company?: string;
  current_company?: string;
  employment_type?: string;
  current_contract_type?: string;
  working_style?: string;
  all_skills?: string[];
  years_at_current_company?: number;
  total_experience_years?: number;
  open_to_work?: boolean;
  linkedin_premium?: boolean;
  current_position?: string;
  education_1?: string;
  location_city?: string;
  location_full?: string;
  [key: string]: any;
}

// Great-circle distance in miles between two lat/lng points (matches the miles
// unit used by the table's radius search).
const haversineMiles = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const R = 3958.8; // Earth radius in miles
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.pow(Math.sin(dLat / 2), 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.pow(Math.sin(dLon / 2), 2);
  return 2 * R * Math.asin(Math.sqrt(a));
};

const Analytics: React.FC<AnalyticsProps> = ({ selectedProject, initialFilters, onNavigateToDataTable }) => {
  const [allCompanies, setAllCompanies] = useState<Company[]>([]);
  const [allCandidates, setAllCandidates] = useState<Candidate[]>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<Company[]>([]);
  const [filteredCandidates, setFilteredCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterState, setFilterState] = useState<FilterState>(
    initialFilters || { groups: [], employmentTypes: [] }
  );
  const [exporting, setExporting] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showPreviewEditor, setShowPreviewEditor] = useState(false);
  const [previewReportData, setPreviewReportData] = useState<any>(null);
  const [previewChartImages, setPreviewChartImages] = useState<{[key: string]: string}>({});

  // Chart type switcher state
  const [chartViews, setChartViews] = useState<Record<string, ChartType>>({
    tenure: 'bar',
    experience: 'doughnut',
    employment: 'doughnut',
    geographic: 'bubble',
    funding: 'bar',
    industry: 'doughnut',
    stage: 'bar',
    revenue: 'bar',
  });

  const setChartView = (key: string, type: ChartType) => {
    setChartViews(prev => ({ ...prev, [key]: type }));
  };

  const analyticsRef = useRef<HTMLDivElement>(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  // Fetch data via the cached analytics endpoint. The backend caches the
  // full candidate + company payloads per project; cache invalidates on
  // any candidate / company write. Cache hit returns immediately; cache
  // miss runs the same SQL the old /api/candidates and /api/companies
  // endpoints did, then stores. Falls back to the legacy two-endpoint
  // fetch if the cached endpoint is unavailable (older deploys).
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // When opened from a candidate radius search, ask the server for just
        // the in-radius subset (candidates within the radius + the companies
        // they work at). Far faster than loading the whole project to filter
        // client-side.
        const r = initialFilters?.radiusSearch;
        const radiusQS = (r && r.type === 'candidate' && r.coords)
          ? `?candidateLat=${r.coords.lat}&candidateLng=${r.coords.lng}&distanceMiles=${r.distance}`
          : '';

        const cachedRes = await fetch(
          `/api/analytics/${selectedProject.id}/data${radiusQS}`,
          { headers: getAuthHeaders() }
        );

        if (cachedRes.ok) {
          const payload = await cachedRes.json();
          setAllCompanies(payload.companies || []);
          setAllCandidates(payload.candidates || []);
          return;
        }

        // 404 / 500 from the cached endpoint — fall back so the page
        // still works on a server without migration 098 applied.
        const [companiesRes, candidatesRes] = await Promise.all([
          fetch(`/api/companies/${selectedProject.id}`, { headers: getAuthHeaders() }),
          fetch(`/api/candidates/${selectedProject.id}`, { headers: getAuthHeaders() }),
        ]);

        if (companiesRes.ok) {
          const companiesData = await companiesRes.json();
          setAllCompanies(companiesData);
        }

        if (candidatesRes.ok) {
          const candidatesData = await candidatesRes.json();
          setAllCandidates(candidatesData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    selectedProject.id,
    initialFilters?.radiusSearch?.type,
    initialFilters?.radiusSearch?.coords?.lat,
    initialFilters?.radiusSearch?.coords?.lng,
    initialFilters?.radiusSearch?.distance,
  ]);

  // Update filterState when initialFilters prop changes
  useEffect(() => {
    if (initialFilters) {
      setFilterState(initialFilters);
    }
  }, [initialFilters]);

  // Apply filters whenever data or filterState changes
  useEffect(() => {
    if (allCompanies.length === 0 || allCandidates.length === 0) {
      return;
    }

    const radius = filterState.radiusSearch;
    const hasGroupFilters = filterState.groups.length > 0 || filterState.employmentTypes.length > 0;

    let companies = allCompanies;
    let candidates = allCandidates;

    if (hasGroupFilters) {
      companies = filterCompaniesByState(allCompanies, filterState);
      candidates = filterCandidatesByState(allCandidates, filterState);
      if (companies.length < allCompanies.length) {
        const filteredCompanyNames = new Set(companies.map(c => c.company_name));
        candidates = candidates.filter(candidate =>
          filteredCompanyNames.has(candidate.current_company || candidate.company || '')
        );
      }
      // Mirror the table's cross-filter in the other direction too: when
      // candidate filters narrowed the people, only show the companies those
      // people work at (the table's Companies tab does the same server-side).
      if (candidates.length < allCandidates.length) {
        const employerNames = new Set(
          candidates.map(c => (c.current_company || c.company || '').toLowerCase()).filter(Boolean)
        );
        companies = companies.filter(co => employerNames.has((co.company_name || '').toLowerCase()));
      }
    }

    // Radius filter — carried from the table so analytics reflect the same
    // radius-filtered subset. Uses the pre-resolved centre coords (haversine).
    if (radius?.coords) {
      const { lat, lng } = radius.coords;
      const within = (la: any, lo: any) =>
        la != null && lo != null && haversineMiles(lat, lng, Number(la), Number(lo)) <= radius.distance;
      if (radius.type === 'candidate') {
        candidates = candidates.filter(c => within(c.candidate_latitude, c.candidate_longitude));
        // Scope companies to only those the in-radius candidates work at. (The
        // server already does this when it serves the radius subset; this makes
        // it correct on the fallback path / stale data too.)
        const names = new Set(
          candidates.map(c => (c.current_company || c.company || '').toLowerCase()).filter(Boolean)
        );
        companies = companies.filter(co => names.has((co.company_name || '').toLowerCase()));
      } else if (radius.type === 'company') {
        companies = companies.filter(c => within(c.latitude, c.longitude));
        const names = new Set(companies.map(c => c.company_name));
        candidates = candidates.filter(c => names.has(c.current_company || c.company || ''));
      }
    }

    setFilteredCompanies(companies);
    setFilteredCandidates(candidates);
  }, [allCompanies, allCandidates, filterState]);

  // ============================================================================
  // CLIENT-SIDE ANALYTICS COMPUTATION
  // ============================================================================

  const computeAnalyticsForData = (candidates: Candidate[], companies: Company[]) => {

    const uniqueCompanies = Array.from(
      new Map(companies.map(c => [c.company_name, c])).values()
    );

    const totalCandidates = candidates.length;
    const totalCompanies = uniqueCompanies.length;

    // Helper: safe division that avoids Infinity/NaN
    const safeDivide = (num: number, denom: number): number => {
      if (denom === 0 || !isFinite(num) || !isFinite(denom)) return 0;
      const result = num / denom;
      return isFinite(result) ? result : 0;
    };

    // Helper: parse NUMERIC values from PostgreSQL (may arrive as strings)
    const toNum = (v: any): number | null => {
      if (v == null) return null;
      const n = Number(v);
      return isFinite(n) ? n : null;
    };

    // Estimate total experience from available position years.
    // Falls back to summing current + previous position durations.
    const getExperience = (c: Candidate): number | null => {
      const stored = toNum(c.total_experience_years);
      if (stored != null && stored > 0) return stored;

      // Sum up position durations we have
      const current = toNum(c.years_at_current_company);
      const prev1 = toNum(c.previous_position_1_years);
      const prev2 = toNum(c.previous_position_2_years);
      const prev3 = toNum(c.previous_position_3_years);

      const parts = [current, prev1, prev2, prev3].filter((v): v is number => v != null && v > 0);
      if (parts.length === 0) {
        // Last resort: compute from current_position_start_date
        if (c.current_position_start_date) {
          const match = String(c.current_position_start_date).match(/^(\d{4})-?(\d{2})?/);
          if (match) {
            const startYear = parseInt(match[1], 10);
            const startMonth = match[2] ? parseInt(match[2], 10) : 1;
            const now = new Date();
            const years = (now.getFullYear() - startYear) + (now.getMonth() + 1 - startMonth) / 12;
            return years > 0 ? years : null;
          }
        }
        return null;
      }
      return parts.reduce((a, b) => a + b, 0);
    };

    const tenureCandidates = candidates.filter(c => toNum(c.years_at_current_company) != null);
    const avgTenure = safeDivide(
      tenureCandidates.reduce((sum, c) => sum + (toNum(c.years_at_current_company) || 0), 0),
      tenureCandidates.length
    );

    const expCandidates = candidates.map(c => ({ c, exp: getExperience(c) })).filter(x => x.exp != null);
    const avgExperience = safeDivide(
      expCandidates.reduce((sum, x) => sum + (x.exp || 0), 0),
      expCandidates.length
    );

    const flightRiskCount = candidates.filter(c => {
      const t = toNum(c.years_at_current_company);
      return t != null && t >= 2 && t <= 3;
    }).length;

    const openToWorkCount = candidates.filter(c => c.open_to_work === true).length;

    const tenureDistribution = [
      { tenure_range: '<1 year', count: 0 },
      { tenure_range: '1-2 years', count: 0 },
      { tenure_range: '2-3 years (Flight Risk)', count: 0 },
      { tenure_range: '3-5 years', count: 0 },
      { tenure_range: '5+ years', count: 0 },
    ];

    candidates.forEach(c => {
      const tenure = toNum(c.years_at_current_company) || 0;
      if (tenure < 1) tenureDistribution[0].count++;
      else if (tenure < 2) tenureDistribution[1].count++;
      else if (tenure <= 3) tenureDistribution[2].count++;
      else if (tenure <= 5) tenureDistribution[3].count++;
      else tenureDistribution[4].count++;
    });

    const experienceLevels = [
      { experience_level: 'Junior (0-3yr)', count: 0 },
      { experience_level: 'Mid-level (3-7yr)', count: 0 },
      { experience_level: 'Senior (7-12yr)', count: 0 },
      { experience_level: 'Lead/Principal (12+yr)', count: 0 },
    ];

    candidates.forEach(c => {
      const exp = getExperience(c) || 0;
      if (exp < 3) experienceLevels[0].count++;
      else if (exp < 7) experienceLevels[1].count++;
      else if (exp < 12) experienceLevels[2].count++;
      else experienceLevels[3].count++;
    });

    const cityCounts: {[key: string]: number} = {};
    candidates.forEach(c => {
      const city = c.location_city || c.location_full;
      if (city && city.toLowerCase() !== 'unknown') {
        cityCounts[city] = (cityCounts[city] || 0) + 1;
      }
    });

    const geographyData = Object.entries(cityCounts)
      .map(([city, count]) => ({ city, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 20);

    const employmentTypeCounts: {[key: string]: number} = {};
    candidates.forEach(c => {
      const type = c.current_contract_type || c.employment_type;
      if (type && type.toLowerCase() !== 'unknown') {
        employmentTypeCounts[type] = (employmentTypeCounts[type] || 0) + 1;
      }
    });

    const employmentTypes = Object.entries(employmentTypeCounts)
      .map(([employment_type, count]) => ({ employment_type, count }))
      .sort((a, b) => b.count - a.count);

    const contractCandidates = candidates.filter(c =>
      (c.current_contract_type || c.employment_type || '').toLowerCase().includes('contract')
    );
    const permanentCandidates = candidates.filter(c =>
      (c.current_contract_type || c.employment_type || '').toLowerCase().includes('permanent') ||
      (c.current_contract_type || c.employment_type || '').toLowerCase().includes('full')
    );

    const contractWithTenure = contractCandidates.filter(c => c.years_at_current_company != null && isFinite(c.years_at_current_company));
    const contractAvgTenure = safeDivide(
      contractWithTenure.reduce((sum, c) => sum + (c.years_at_current_company || 0), 0),
      contractWithTenure.length
    );

    const permanentWithTenure = permanentCandidates.filter(c => c.years_at_current_company != null && isFinite(c.years_at_current_company));
    const permanentAvgTenure = safeDivide(
      permanentWithTenure.reduce((sum, c) => sum + (c.years_at_current_company || 0), 0),
      permanentWithTenure.length
    );

    const tenureComparison = [
      { type: 'Contract', avg_tenure: contractAvgTenure.toFixed(1) },
      { type: 'Permanent', avg_tenure: permanentAvgTenure.toFixed(1) },
    ];

    const educationCounts: {[key: string]: number} = {};
    candidates.forEach(c => {
      if (c.education_1) {
        educationCounts[c.education_1] = (educationCounts[c.education_1] || 0) + 1;
      }
    });

    const educationData = Object.entries(educationCounts)
      .map(([school, count]) => ({ school, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 15);

    const companyTenureCounts: {[key: string]: {count: number, totalTenure: number}} = {};
    candidates.forEach(c => {
      const company = c.current_company || c.company;
      if (company && company.toLowerCase() !== 'unknown') {
        if (!companyTenureCounts[company]) {
          companyTenureCounts[company] = { count: 0, totalTenure: 0 };
        }
        companyTenureCounts[company].count++;
        companyTenureCounts[company].totalTenure += (c.years_at_current_company || 0);
      }
    });

    const companyTenure = Object.entries(companyTenureCounts)
      .map(([company, data]) => ({
        company,
        count: data.count,
        avg_tenure: data.count > 0 ? (data.totalTenure / data.count).toFixed(1) : '0'
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 15);

    const talentSource = companyTenure.slice(0, 10);

    // Company enrichment data — filter out "Unknown" from all
    const fundingCounts: {[key: string]: number} = {};
    uniqueCompanies.forEach(c => {
      if (c.funding_range && !c.funding_range.toLowerCase().includes('unknown')) {
        fundingCounts[c.funding_range] = (fundingCounts[c.funding_range] || 0) + 1;
      }
    });
    const fundingData = Object.entries(fundingCounts)
      .map(([range, count]) => ({ range, count }))
      .sort((a, b) => a.range.localeCompare(b.range));

    // Industry data (replaces business type)
    const industryCounts: {[key: string]: number} = {};
    uniqueCompanies.forEach(c => {
      if (c.industry && c.industry.toLowerCase() !== 'unknown') {
        industryCounts[c.industry] = (industryCounts[c.industry] || 0) + 1;
      }
    });
    const industryData = Object.entries(industryCounts)
      .map(([industry, count]) => ({ industry, count }))
      .sort((a, b) => b.count - a.count);

    const stageCounts: {[key: string]: number} = {};
    uniqueCompanies.forEach(c => {
      if (c.business_stage && c.business_stage.toLowerCase() !== 'unknown') {
        stageCounts[c.business_stage] = (stageCounts[c.business_stage] || 0) + 1;
      }
    });
    const stageData = Object.entries(stageCounts)
      .map(([stage, count]) => ({ stage, count }));

    const revenueCounts: {[key: string]: number} = {};
    uniqueCompanies.forEach(c => {
      if (c.annual_revenue && c.annual_revenue.toLowerCase() !== 'unknown') {
        revenueCounts[c.annual_revenue] = (revenueCounts[c.annual_revenue] || 0) + 1;
      }
    });
    const revenueData = Object.entries(revenueCounts)
      .map(([range, count]) => ({ range, count }))
      .sort((a, b) => a.range.localeCompare(b.range));

    // ── Keyword map: what this market actually talks about ──────────────
    // Structured sources only (accurate counts, no free-text soup):
    // skills from all_skills arrays, terms from current job titles.
    const TITLE_STOPWORDS = new Set([
      'of', 'and', 'the', 'for', 'at', 'in', 'to', 'a', 'an', '&', '-', '–', '/', '|',
      'i', 'ii', 'iii', 'iv', 'jr', 'sr',
    ]);
    const skillCounts = new Map<string, number>();
    const titleCounts = new Map<string, number>();
    const canon = new Map<string, string>(); // lowercase -> display casing (first seen)
    candidates.forEach(c => {
      if (Array.isArray(c.all_skills)) {
        // Dedupe within one candidate so a repeated skill counts once per person
        const seen = new Set<string>();
        c.all_skills.forEach(s => {
          const t = String(s || '').trim();
          if (t.length < 2 || t.length > 40) return;
          const k = t.toLowerCase();
          if (seen.has(k)) return;
          seen.add(k);
          if (!canon.has(k)) canon.set(k, t);
          skillCounts.set(k, (skillCounts.get(k) || 0) + 1);
        });
      }
      const title = (c as any).current_position || (c as any).position || '';
      if (title) {
        const seen = new Set<string>();
        String(title).split(/[^A-Za-z0-9+#.]+/).forEach(w => {
          const t = w.trim();
          if (t.length < 2 || t.length > 30) return;
          const k = t.toLowerCase();
          if (TITLE_STOPWORDS.has(k) || /^\d+$/.test(k) || seen.has(k)) return;
          seen.add(k);
          if (!canon.has(k)) canon.set(k, t);
          titleCounts.set(k, (titleCounts.get(k) || 0) + 1);
        });
      }
    });
    const keywordData = [
      ...Array.from(skillCounts.entries()).map(([k, count]) => ({ key: k, term: canon.get(k) || k, count, type: 'skill' as const })),
      ...Array.from(titleCounts.entries())
        .filter(([k]) => !skillCounts.has(k)) // a term that's also a skill shows once, as a skill
        .map(([k, count]) => ({ key: k, term: canon.get(k) || k, count, type: 'title' as const })),
    ]
      .filter(x => x.count >= 2)
      .sort((a, b) => b.count - a.count)
      .slice(0, 40);

    return {
      keywordData,
      marketOverview: {
        total_candidates: totalCandidates,
        total_companies: totalCompanies,
        avg_tenure: avgTenure.toFixed(1),
        avg_experience: avgExperience.toFixed(1),
        flight_risk_count: flightRiskCount,
        open_to_work_count: openToWorkCount,
      },
      tenureDistribution,
      experienceLevels,
      geographyData,
      employmentTypes,
      tenureComparison,
      educationData,
      companyTenure,
      talentSource,
      uniqueCompanies,
      fundingData,
      industryData,
      stageData,
      revenueData,
    };
  };

  const computeAnalytics = () => {
    return computeAnalyticsForData(filteredCandidates, filteredCompanies);
  };

  const analytics = computeAnalytics();

  // ============================================================================
  // PDF EXPORT (AI Report)
  // ============================================================================

  const handleGenerateAIReport = async (config: ReportConfig) => {
    setExporting(true);

    try {
      let parsedFilters: FilterState = { groups: [], employmentTypes: [] };

      if (config.reportPurpose && config.reportPurpose.trim().length > 0) {
        const parseResponse = await fetch('/api/ai-search/parse-report-query', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ query: config.reportPurpose })
        });

        if (parseResponse.ok) {
          const parseData = await parseResponse.json();
          if (parseData.success && parseData.filters) {
            parsedFilters = parseData.filters;
          }
        }
      }

      const reportFilteredCompanies = parsedFilters.groups.length > 0
        ? filterCompaniesByState(allCompanies, parsedFilters)
        : filteredCompanies;

      const reportFilteredCandidates = parsedFilters.groups.length > 0
        ? filterCandidatesByState(allCandidates, parsedFilters)
        : filteredCandidates;

      const reportAnalytics = computeAnalyticsForData(reportFilteredCandidates, reportFilteredCompanies);

      const hasFilters = filterState.groups.length > 0 || filterState.employmentTypes.length > 0;
      let filterSummary = '';

      if (hasFilters) {
        const filterDescriptions: string[] = [];
        filterState.groups.forEach(group => {
          group.conditions.forEach(condition => {
            const field = condition.column.replace(/_/g, ' ');
            const operator = condition.operator.replace(/_/g, ' ');
            filterDescriptions.push(`${field} ${operator} "${condition.value}"`);
          });
        });
        if (filterState.employmentTypes.length > 0) {
          filterDescriptions.push(`Employment: ${filterState.employmentTypes.join(', ')}`);
        }
        filterSummary = filterDescriptions.slice(0, 5).join(' • ');
        if (filterDescriptions.length > 5) {
          filterSummary += ` • +${filterDescriptions.length - 5} more filters`;
        }
      }

      if (config.reportPurpose) {
        filterSummary = config.reportPurpose + (filterSummary ? ` (${filterSummary})` : '');
      }

      const response = await fetch('/api/ai-search/generate-report', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          analytics: reportAnalytics,
          reportPurpose: config.reportPurpose,
          audienceType: config.audienceType,
          filterSummary
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate AI report');
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'AI generation failed');
      }

      // Chart data is now passed directly to the report (no screenshot capture needed)
      const chartImages: {[key: string]: string} = {};

      const uniqueAllCompaniesCount = Array.from(
        new Map(allCompanies.map(c => [c.company_name, c])).values()
      ).length;

      const reportData = {
        projectName: selectedProject.name,
        dateGenerated: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        candidatesCount: reportAnalytics.marketOverview.total_candidates,
        companiesCount: reportAnalytics.marketOverview.total_companies,
        totalCandidates: allCandidates.length,
        totalCompanies: uniqueAllCompaniesCount,
        hasFilters,
        filterSummary,
        additionalContext: config.reportPurpose || '',
        sections: [
          {
            title: 'Tenure Distribution', chartKey: 'tenure', insights: data.content.tenure_insights || [], enabled: true,
            chartType: 'doughnut' as const,
            chartData: reportAnalytics.tenureDistribution.map((t: any) => ({ label: t.tenure_range.replace(' (Flight Risk)', ''), value: t.count })),
            centerLabel: 'Total',
          },
          {
            title: 'Experience Levels', chartKey: 'experience', insights: data.content.experience_insights || [], enabled: true,
            chartType: 'doughnut' as const,
            chartData: reportAnalytics.experienceLevels.map((e: any) => ({ label: e.experience_level, value: e.count })),
            centerLabel: 'Total',
          },
          {
            title: 'Geographic Distribution', chartKey: 'geographic', insights: data.content.geographic_insights || [], enabled: true,
            chartType: 'bar' as const,
            chartData: reportAnalytics.geographyData.slice(0, 12).map((g: any) => ({ label: g.city, value: g.count })),
          },
          {
            title: 'Employment Types', chartKey: 'employment', insights: data.content.employment_insights || [], enabled: true,
            chartType: 'doughnut' as const,
            chartData: reportAnalytics.employmentTypes.map((e: any) => ({ label: e.employment_type, value: e.count })),
            centerLabel: 'Total',
          },
          {
            title: 'Top Companies', chartKey: 'companies', insights: data.content.companies_insights || [], enabled: true,
            chartType: 'ranked' as const,
            chartData: reportAnalytics.talentSource.map((s: any) => ({ label: s.company, value: s.count, subtitle: `${s.avg_tenure} yrs avg tenure` })),
          },
        ],
        careerProgression: {
          avgTenure: `${reportAnalytics.marketOverview.avg_tenure} years`,
          avgExperience: `${reportAnalytics.marketOverview.avg_experience} years`
        }
      };

      setPreviewReportData(reportData);
      setPreviewChartImages(chartImages);
      setShowReportModal(false);
      setShowPreviewEditor(true);

    } catch (error) {
      console.error('AI report generation error:', error);
      alert('Failed to generate AI report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const exportToPDF = async () => {
    if (!analyticsRef.current) return;

    setExporting(true);

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - (margin * 2);

      // PAGE 1: Cover + Executive Summary
      pdf.setFontSize(22);
      pdf.setTextColor(31, 41, 55);
      pdf.text('Market Intelligence Report', pageWidth / 2, 25, { align: 'center' });

      pdf.setFontSize(14);
      pdf.setTextColor(75, 85, 99);
      pdf.text(selectedProject.name, pageWidth / 2, 35, { align: 'center' });

      pdf.setFontSize(10);
      const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      pdf.text(`Generated: ${date}`, pageWidth / 2, 42, { align: 'center' });

      let yPos = 55;
      if (filterState.groups.length > 0 || filterState.employmentTypes.length > 0) {
        pdf.setFontSize(9);
        pdf.setTextColor(59, 130, 246);
        pdf.text('Filters Applied:', margin, yPos);
        yPos += 5;

        pdf.setFontSize(8);
        pdf.setTextColor(75, 85, 99);
        const filterSummary: string[] = [];
        filterState.groups.forEach((group, i) => {
          filterSummary.push(`${group.name || `Group ${i + 1}`} (${group.conditions.length})`);
        });
        if (filterState.employmentTypes.length > 0) {
          filterSummary.push(`Employment: ${filterState.employmentTypes.join(', ')}`);
        }
        pdf.text(filterSummary.join(' • '), margin, yPos);
        yPos += 8;
      }

      // Metrics grid
      pdf.setFontSize(12);
      pdf.setTextColor(31, 41, 55);
      pdf.text('Executive Summary', margin, yPos);
      yPos += 8;

      const metrics = [
        { label: 'Total Candidates', value: analytics.marketOverview.total_candidates, color: [59, 130, 246] },
        { label: 'Total Companies', value: analytics.marketOverview.total_companies, color: [16, 185, 129] },
        { label: 'Avg Tenure', value: `${analytics.marketOverview.avg_tenure} yrs`, color: [139, 92, 246] },
        { label: 'Avg Experience', value: `${analytics.marketOverview.avg_experience} yrs`, color: [245, 158, 11] },
        { label: 'Flight Risk', value: `${analytics.marketOverview.flight_risk_count} (${flightRiskPct}%)`, color: [239, 68, 68] },
        { label: 'Open to Work', value: `${analytics.marketOverview.open_to_work_count} (${openToWorkPct}%)`, color: [34, 197, 94] },
      ];

      const boxWidth = (contentWidth - 5) / 2;
      const boxHeight = 20;
      let boxX = margin;
      let boxY = yPos;

      metrics.forEach((metric, i) => {
        if (i % 2 === 0 && i > 0) {
          boxY += boxHeight + 3;
          boxX = margin;
        }

        pdf.setDrawColor(200, 200, 200);
        pdf.setFillColor(250, 250, 250);
        pdf.rect(boxX, boxY, boxWidth, boxHeight, 'FD');

        pdf.setFontSize(8);
        pdf.setTextColor(75, 85, 99);
        pdf.text(metric.label, boxX + 3, boxY + 7);

        pdf.setFontSize(14);
        pdf.setTextColor(metric.color[0], metric.color[1], metric.color[2]);
        pdf.text(String(metric.value), boxX + 3, boxY + 15);

        boxX += boxWidth + 5;
      });

      yPos = boxY + boxHeight + 10;

      pdf.setFontSize(10);
      pdf.setTextColor(31, 41, 55);
      pdf.text('Key Market Insights:', margin, yPos);
      yPos += 6;

      pdf.setFontSize(8);
      pdf.setTextColor(75, 85, 99);
      const insights = [
        `• ${analytics.tenureDistribution[2].count} candidates in prime flight risk zone (2-3 years tenure)`,
        `• Top location: ${analytics.geographyData[0]?.city || 'N/A'} with ${analytics.geographyData[0]?.count || 0} candidates`,
        `• ${analytics.employmentTypes.length} different employment types in market`,
        `• ${analytics.marketOverview.total_companies} companies represented`,
      ];

      insights.forEach(insight => {
        pdf.text(insight, margin + 2, yPos);
        yPos += 5;
      });

      // PAGES 2-3: Charts (4 per page in 2x2 grid)
      const chartContainers = analyticsRef.current.querySelectorAll('.chart-container');
      const selectedCharts: HTMLElement[] = [];

      const chartTitles = [
        'Tenure Distribution',
        'Experience Levels',
        'Top Candidate Locations',
        'Employment Type Distribution',
        'Contract vs Permanent Tenure',
        'Funding Distribution',
        'Industry Distribution',
        'Business Stage Distribution',
      ];

      chartContainers.forEach((container) => {
        const title = (container as HTMLElement).querySelector('h3')?.textContent;
        if (title && chartTitles.includes(title)) {
          selectedCharts.push(container as HTMLElement);
        }
      });

      for (let i = 0; i < Math.min(8, selectedCharts.length); i += 4) {
        pdf.addPage();

        const chartWidth = (contentWidth - 5) / 2;
        const chartHeight = (pageHeight - margin * 2 - 5) / 2;

        for (let j = 0; j < 4 && (i + j) < selectedCharts.length; j++) {
          const chartElement = selectedCharts[i + j];

          const canvas = await html2canvas(chartElement, {
            scale: 1.5,
            backgroundColor: '#ffffff',
          });

          const imgData = canvas.toDataURL('image/png');

          const col = j % 2;
          const row = Math.floor(j / 2);
          const x = margin + col * (chartWidth + 5);
          const y = margin + row * (chartHeight + 5);

          pdf.addImage(imgData, 'PNG', x, y, chartWidth, chartHeight);
        }
      }

      // Footer on all pages
      const totalPages = pdf.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(156, 163, 175);
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
        pdf.text(`Powered by ${BRAND.name}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
      }

      const filename = `Market_Intelligence_${selectedProject.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-3 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          <div className="text-sm font-medium" style={{ color: SURFACE.textSecondary }}>Loading market intelligence...</div>
        </div>
      </div>
    );
  }

  const hasActiveFilters = filterState.groups.length > 0 || filterState.employmentTypes.length > 0;
  const flightRiskPct = analytics.marketOverview.total_candidates > 0
    ? ((analytics.marketOverview.flight_risk_count / analytics.marketOverview.total_candidates) * 100).toFixed(1)
    : '0';
  const openToWorkPct = analytics.marketOverview.total_candidates > 0
    ? ((analytics.marketOverview.open_to_work_count / analytics.marketOverview.total_candidates) * 100).toFixed(1)
    : '0';

  const uniqueAllCompanies = Array.from(
    new Map(allCompanies.map(c => [c.company_name, c])).values()
  );
  const uniqueFilteredCompanies = Array.from(
    new Map(filteredCompanies.map(c => [c.company_name, c])).values()
  );

  // Section header component
  const SectionHeader = ({ title, subtitle }: { title: string; subtitle?: string }) => (
    <div className="mb-5">
      <h2 className="text-xl font-bold tracking-tight" style={{ color: SURFACE.text }}>{title}</h2>
      {subtitle && <p className="text-sm mt-0.5" style={{ color: SURFACE.textMuted }}>{subtitle}</p>}
    </div>
  );

  // Chart card wrapper with optional type switcher
  const ChartCard = ({ title, children, className = '', span = 1, chartKey, switcherOptions }: {
    title: string;
    children: React.ReactNode;
    className?: string;
    span?: number;
    chartKey?: string;
    switcherOptions?: ChartType[];
  }) => (
    <div
      className={`chart-container card p-6 ${span === 2 ? 'md:col-span-2' : ''} ${className}`}
      style={{
        background: SURFACE.card,
      }}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="section-label" style={{ color: SURFACE.textSecondary }}>
          {title}
        </h3>
        {chartKey && switcherOptions && switcherOptions.length > 1 && (
          <ChartTypeSwitcher
            options={switcherOptions}
            active={chartViews[chartKey] || switcherOptions[0]}
            onChange={(type) => setChartView(chartKey, type)}
          />
        )}
      </div>
      <div className="chart-content">
        {children}
      </div>
    </div>
  );

  // Insight box
  const InsightBox = ({ text, color = 'blue' }: { text: string; color?: string }) => {
    const colorMap: Record<string, { bg: string; border: string; text: string }> = {
      blue: { bg: '#EFF6FF', border: '#BFDBFE', text: '#1E40AF' },
      amber: { bg: '#FFFBEB', border: '#FDE68A', text: '#92400E' },
      green: { bg: '#ECFDF5', border: '#A7F3D0', text: '#065F46' },
    };
    const c = colorMap[color] || colorMap.blue;
    return (
      <div
        className="mt-4 px-4 py-3 rounded-lg text-sm"
        style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.text }}
      >
        {text}
      </div>
    );
  };

  return (
    <div style={{ background: SURFACE.page, minHeight: '100vh' }}>
      {/* Filter info banner */}
      {hasActiveFilters && (
        <div className="border-b px-6 py-3" style={{ background: '#EFF6FF', borderColor: '#BFDBFE' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium" style={{ color: '#1D4ED8' }}>
                Showing analytics for filtered data
              </span>
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: '#DBEAFE', color: '#1E40AF' }}>
                {filteredCandidates.length} candidates, {uniqueFilteredCompanies.length} companies
              </span>
            </div>
            {onNavigateToDataTable ? (
              <button
                onClick={() => onNavigateToDataTable(filterState)}
                className="text-xs font-medium underline cursor-pointer"
                style={{ color: '#2563EB' }}
              >
                Back to Data Table
              </button>
            ) : (
              <span className="text-xs" style={{ color: '#3B82F6' }}>
                Go back to Data Table to modify filters
              </span>
            )}
          </div>
        </div>
      )}

      <div className="p-6" ref={analyticsRef}>
        <div className="max-w-7xl mx-auto">

          {/* Page Header */}
          <div className="mb-8 flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold tracking-tight" style={{ color: SURFACE.text }}>Market Intelligence</h1>
              <p className="text-sm mt-1" style={{ color: SURFACE.textMuted }}>
                Deep insights into your recruitment territory
              </p>
              {hasActiveFilters && (
                <div className="text-xs mt-2 px-2 py-1 rounded inline-block" style={{ background: '#DBEAFE', color: '#1E40AF' }}>
                  Filtered: {filteredCandidates.length} of {allCandidates.length} candidates, {uniqueFilteredCompanies.length} of {uniqueAllCompanies.length} companies
                </div>
              )}
            </div>
            <button
              onClick={() => setShowReportModal(true)}
              disabled={exporting}
              className="btn-primary px-5 py-2.5 flex items-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
                boxShadow: '0 1px 3px rgba(37, 99, 235, 0.3)',
              }}
            >
              {exporting ? (
                <span>Generating Report...</span>
              ) : (
                <span>Generate AI Report</span>
              )}
            </button>
          </div>

          {/* ================================================================ */}
          {/* KPI CARDS */}
          {/* ================================================================ */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
            <KPICard
              label="Total Candidates"
              value={analytics.marketOverview.total_candidates}
              subtitle={`${analytics.marketOverview.total_companies} companies`}
              colorIndex={0}
            />
            <KPICard
              label="Avg Tenure"
              value={`${analytics.marketOverview.avg_tenure} yrs`}
              subtitle="at current company"
              colorIndex={1}
            />
            <KPICard
              label="Avg Experience"
              value={`${analytics.marketOverview.avg_experience} yrs`}
              subtitle="total career"
              colorIndex={3}
            />
            <KPICard
              label="Flight Risk"
              value={`${flightRiskPct}%`}
              subtitle={`${analytics.marketOverview.flight_risk_count} candidates`}
              colorIndex={2}
            />
            <KPICard
              label="Open to Work"
              value={`${openToWorkPct}%`}
              subtitle={`${analytics.marketOverview.open_to_work_count} candidates`}
              colorIndex={4}
            />
          </div>

          {/* ================================================================ */}
          {/* KEYWORD MAP — what this market talks about */}
          {/* ================================================================ */}
          {analytics.keywordData && analytics.keywordData.length > 0 && (() => {
            const kws = analytics.keywordData;
            const maxCount = kws[0]?.count || 1;
            const minCount = kws[kws.length - 1]?.count || 1;
            // sqrt scale keeps the tail readable while the head dominates —
            // wide range + weight scaling gives the word-cloud drama without
            // a cloud's random placement or area distortion.
            const t = (count: number) => Math.sqrt((count - minCount) / Math.max(1, maxCount - minCount));
            const scale = (count: number) => 14 + t(count) * 30;          // 14px → 44px
            const weight = (count: number) => 500 + Math.round(t(count) * 3) * 100; // 500 → 800
            // Bigger terms sit darker; the tail fades — rank made visible.
            const shade = (count: number, type: 'skill' | 'title') => {
              const shades = type === 'skill'
                ? ['#93C5FD', '#60A5FA', '#3B82F6', '#1D4ED8']
                : ['#C4B5FD', '#A78BFA', '#8B5CF6', '#6D28D9'];
              return shades[Math.min(3, Math.round(t(count) * 3))];
            };
            const clickKeyword = (kw: { key: string; term: string; type: 'skill' | 'title' }) => {
              if (!onNavigateToDataTable) return;
              const column = kw.type === 'skill' ? 'all_skills' : 'current_position';
              onNavigateToDataTable({
                ...filterState,
                groups: [
                  ...filterState.groups,
                  {
                    id: `kw-${kw.key}-${Date.now()}`,
                    name: `Keyword: ${kw.term}`,
                    logic: 'AND' as const,
                    entityType: 'candidate' as const,
                    conditions: [{
                      id: `kw-${kw.key}-c`,
                      column,
                      operator: 'contains' as const,
                      value: kw.term,
                      entityType: 'candidate' as const,
                    }],
                  },
                ],
              });
            };
            return (
              <>
                <SectionHeader title="Keyword Map" subtitle="The skills and titles this market talks about — sized by how many candidates mention them. Click any keyword to see those people." />
                <div className="grid grid-cols-1 gap-5 mb-10">
                  <ChartCard title={`Top keywords across ${analytics.marketOverview.total_candidates.toLocaleString('en-GB')} candidates`} span={2}>
                    <div className="flex flex-wrap items-baseline justify-center gap-x-5 gap-y-2 py-6 px-4">
                      {kws.map(kw => (
                        <button
                          key={kw.key}
                          onClick={() => clickKeyword(kw)}
                          className="group relative inline-flex items-baseline leading-none transition-all hover:-translate-y-0.5"
                          style={{ fontSize: `${scale(kw.count)}px`, fontWeight: weight(kw.count), color: shade(kw.count, kw.type) }}
                          title={`${kw.count.toLocaleString('en-GB')} candidates ${kw.type === 'skill' ? 'list this skill' : 'have this in their job title'} — click to filter the table to them`}
                        >
                          <span className="group-hover:underline decoration-2 underline-offset-4">{kw.term}</span>
                          <span className="hidden group-hover:inline-block ml-1.5 px-1.5 py-0.5 rounded-full bg-gray-900 text-white font-semibold align-middle"
                            style={{ fontSize: '11px' }}>
                            {kw.count.toLocaleString('en-GB')}
                          </span>
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100 text-[11px] text-gray-500">
                      <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-blue-200 border border-blue-300 inline-block" /> Skill</span>
                      <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-violet-200 border border-violet-300 inline-block" /> Job-title term</span>
                      <span className="ml-auto">Counts respect your active filters</span>
                    </div>
                  </ChartCard>
                </div>
              </>
            );
          })()}

          {/* ================================================================ */}
          {/* CAREER INTELLIGENCE */}
          {/* ================================================================ */}
          <SectionHeader title="Career Intelligence" subtitle="Tenure, experience and career progression insights" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">

            {/* Tenure Distribution */}
            <ChartCard title="Tenure Distribution" chartKey="tenure" switcherOptions={['bar', 'doughnut', 'horizontalBar']}>
              {chartViews.tenure === 'doughnut' ? (
                <PremiumDoughnut
                  data={analytics.tenureDistribution.map(t => ({
                    label: t.tenure_range.replace(' (Flight Risk)', ''),
                    value: t.count,
                  }))}
                  centerLabel="Total"
                  height={360}
                />
              ) : chartViews.tenure === 'horizontalBar' ? (
                <PremiumBarChart
                  data={analytics.tenureDistribution.map(t => ({
                    label: t.tenure_range.replace(' (Flight Risk)', ''),
                    value: t.count,
                  }))}
                  height={260}
                  horizontal
                  multiColor
                />
              ) : (
                <PremiumBarChart
                  data={analytics.tenureDistribution.map(t => ({
                    label: t.tenure_range.replace(' (Flight Risk)', ''),
                    value: t.count,
                  }))}
                  height={260}
                  multiColor
                  className="mt-2"
                />
              )}
              <InsightBox
                text={`${analytics.tenureDistribution[2].count} candidates in the 2-3 year prime flight risk zone — ready to move.`}
                color="amber"
              />
            </ChartCard>

            {/* Experience Levels */}
            <ChartCard title="Experience Levels" chartKey="experience" switcherOptions={['doughnut', 'bar', 'horizontalBar']}>
              {chartViews.experience === 'bar' ? (
                <PremiumBarChart
                  data={analytics.experienceLevels.map(e => ({
                    label: e.experience_level,
                    value: e.count,
                  }))}
                  height={280}
                  multiColor
                />
              ) : chartViews.experience === 'horizontalBar' ? (
                <PremiumBarChart
                  data={analytics.experienceLevels.map(e => ({
                    label: e.experience_level,
                    value: e.count,
                  }))}
                  height={280}
                  horizontal
                  multiColor
                />
              ) : (
                <PremiumDoughnut
                  data={analytics.experienceLevels.map(e => ({
                    label: e.experience_level,
                    value: e.count,
                  }))}
                  centerLabel="Total"
                  height={360}
                />
              )}
            </ChartCard>

            {/* Career Progression */}
            <ChartCard title="Career Progression" span={2}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <MetricCard
                  label="Avg Tenure"
                  value={`${analytics.marketOverview.avg_tenure} yrs`}
                  subtitle="at current company"
                  colorIndex={1}
                />
                <MetricCard
                  label="Avg Experience"
                  value={`${analytics.marketOverview.avg_experience} yrs`}
                  subtitle="total career"
                  colorIndex={5}
                />
              </div>
            </ChartCard>
          </div>

          {/* ================================================================ */}
          {/* GEOGRAPHIC DISTRIBUTION (detail) */}
          {/* ================================================================ */}
          <SectionHeader title="Geographic Distribution" subtitle="Where your talent pool is concentrated" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
            <ChartCard title="Top Candidate Locations" chartKey="geographic" switcherOptions={['bubble', 'bar', 'horizontalBar']}>
              {chartViews.geographic === 'bar' ? (
                <PremiumBarChart
                  data={analytics.geographyData.slice(0, 12).map(g => ({
                    label: g.city,
                    value: g.count,
                  }))}
                  height={380}
                  colorIndex={0}
                  multiColor
                />
              ) : chartViews.geographic === 'horizontalBar' ? (
                <PremiumBarChart
                  data={analytics.geographyData.slice(0, 12).map(g => ({
                    label: g.city,
                    value: g.count,
                  }))}
                  height={380}
                  horizontal
                  colorIndex={0}
                />
              ) : (
                <BubbleChart
                  data={analytics.geographyData.map(g => ({
                    label: g.city,
                    value: g.count,
                  }))}
                  height={380}
                  maxBubbles={15}
                />
              )}
            </ChartCard>
            <ChartCard title="Location Ranking">
              <RankedList
                data={analytics.geographyData.slice(0, 12).map(g => ({
                  label: g.city,
                  value: g.count,
                }))}
                valueLabel="candidates"
                colorIndex={0}
              />
            </ChartCard>
          </div>

          {/* ================================================================ */}
          {/* CONTRACT MARKET INTELLIGENCE */}
          {/* ================================================================ */}
          <SectionHeader title="Contract Market Intelligence" subtitle="Employment types and contract analysis" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">

            {/* Employment Type Distribution */}
            <ChartCard title="Employment Type Distribution" chartKey="employment" switcherOptions={['doughnut', 'bar', 'horizontalBar']}>
              {chartViews.employment === 'bar' ? (
                <PremiumBarChart
                  data={analytics.employmentTypes.map(e => ({
                    label: e.employment_type,
                    value: e.count,
                  }))}
                  height={280}
                  multiColor
                />
              ) : chartViews.employment === 'horizontalBar' ? (
                <PremiumBarChart
                  data={analytics.employmentTypes.map(e => ({
                    label: e.employment_type,
                    value: e.count,
                  }))}
                  height={280}
                  horizontal
                  multiColor
                />
              ) : (
                <PremiumDoughnut
                  data={analytics.employmentTypes.map(e => ({
                    label: e.employment_type,
                    value: e.count,
                  }))}
                  centerLabel="Total"
                  height={360}
                />
              )}
            </ChartCard>

            {/* Contract vs Permanent Tenure */}
            <ChartCard title="Contract vs Permanent Tenure">
              <PremiumBarChart
                data={analytics.tenureComparison.map(t => ({
                  label: t.type,
                  value: parseFloat(t.avg_tenure),
                }))}
                height={260}
                multiColor
                formatValue={(v) => `${v.toFixed(1)} yrs`}
                className="mt-4"
              />
              <InsightBox
                text={`Contract workers average ${analytics.tenureComparison[0].avg_tenure} years vs ${analytics.tenureComparison[1].avg_tenure} years for permanent staff.`}
                color="blue"
              />
            </ChartCard>
          </div>

          {/* ================================================================ */}
          {/* EDUCATION & TOP COMPANIES */}
          {/* ================================================================ */}
          <SectionHeader title="Education & Top Companies" subtitle="Where your talent studied and works" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">

            {/* Top Education Sources */}
            <ChartCard title="Top Education Sources">
              <RankedList
                data={analytics.educationData.slice(0, 10).map(edu => ({
                  label: edu.school,
                  value: edu.count,
                }))}
                valueLabel="graduates"
                colorIndex={6}
              />
            </ChartCard>

            {/* Top Talent Source Companies */}
            <ChartCard title="Top Talent Source Companies">
              <RankedList
                data={analytics.talentSource.map(source => ({
                  label: source.company,
                  value: source.count,
                  subtitle: `${source.avg_tenure} yrs avg tenure`,
                }))}
                valueLabel="people"
                colorIndex={0}
              />
              <InsightBox
                text="These companies are losing talent — perfect for recruitment partnerships and BD outreach."
                color="blue"
              />
            </ChartCard>
          </div>

          {/* ================================================================ */}
          {/* COMPANY MARKET INTELLIGENCE */}
          {/* ================================================================ */}
          <SectionHeader title="Company Market Intelligence" subtitle="Industry, funding and revenue analysis" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">

            {/* Industry Distribution (replaces Business Type) */}
            {analytics.industryData.length > 0 && (
              <ChartCard title="Industry Distribution" chartKey="industry" switcherOptions={['doughnut', 'bar', 'horizontalBar']}>
                {chartViews.industry === 'bar' ? (
                  <PremiumBarChart
                    data={analytics.industryData.slice(0, 12).map(ind => ({
                      label: ind.industry,
                      value: ind.count,
                    }))}
                    height={280}
                    multiColor
                  />
                ) : chartViews.industry === 'horizontalBar' ? (
                  <PremiumBarChart
                    data={analytics.industryData.slice(0, 12).map(ind => ({
                      label: ind.industry,
                      value: ind.count,
                    }))}
                    height={280}
                    horizontal
                    colorIndex={5}
                  />
                ) : (
                  <PremiumDoughnut
                    data={analytics.industryData.slice(0, 10).map(ind => ({
                      label: ind.industry,
                      value: ind.count,
                    }))}
                    centerLabel="Industries"
                    height={360}
                  />
                )}
              </ChartCard>
            )}

            {/* Funding Distribution */}
            {analytics.fundingData.length > 0 && (
              <ChartCard title="Funding Distribution" chartKey="funding" switcherOptions={['bar', 'doughnut', 'horizontalBar']}>
                {chartViews.funding === 'doughnut' ? (
                  <PremiumDoughnut
                    data={analytics.fundingData.map(f => ({
                      label: f.range,
                      value: f.count,
                    }))}
                    centerLabel="Companies"
                    height={360}
                  />
                ) : chartViews.funding === 'horizontalBar' ? (
                  <PremiumBarChart
                    data={analytics.fundingData.map(f => ({
                      label: f.range,
                      value: f.count,
                    }))}
                    height={260}
                    horizontal
                    colorIndex={1}
                  />
                ) : (
                  <PremiumBarChart
                    data={analytics.fundingData.map(f => ({
                      label: f.range,
                      value: f.count,
                    }))}
                    height={260}
                    colorIndex={1}
                  />
                )}
              </ChartCard>
            )}

            {/* Business Stage Distribution */}
            {analytics.stageData.length > 0 && (
              <ChartCard title="Business Stage Distribution" chartKey="stage" switcherOptions={['bar', 'doughnut']}>
                {chartViews.stage === 'doughnut' ? (
                  <PremiumDoughnut
                    data={analytics.stageData.map(s => ({
                      label: s.stage,
                      value: s.count,
                    }))}
                    centerLabel="Companies"
                    height={360}
                  />
                ) : (
                  <PremiumBarChart
                    data={analytics.stageData.map(s => ({
                      label: s.stage,
                      value: s.count,
                    }))}
                    height={260}
                    colorIndex={3}
                  />
                )}
              </ChartCard>
            )}

            {/* Annual Revenue Distribution */}
            {analytics.revenueData.length > 0 && (
              <ChartCard title="Annual Revenue Distribution" chartKey="revenue" switcherOptions={['bar', 'doughnut']}>
                {chartViews.revenue === 'doughnut' ? (
                  <PremiumDoughnut
                    data={analytics.revenueData.map(r => ({
                      label: r.range,
                      value: r.count,
                    }))}
                    centerLabel="Companies"
                    height={360}
                  />
                ) : (
                  <PremiumBarChart
                    data={analytics.revenueData.map(r => ({
                      label: r.range,
                      value: r.count,
                    }))}
                    height={260}
                    colorIndex={2}
                  />
                )}
              </ChartCard>
            )}
          </div>

        </div>
      </div>

      {/* AI Report Configuration Modal */}
      <AIReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        onGenerate={handleGenerateAIReport}
        isGenerating={exporting}
      />

      {/* Report Visual Preview & Editor */}
      {previewReportData && (
        <ReportPreviewEditor
          isOpen={showPreviewEditor}
          reportData={previewReportData}
          chartImages={previewChartImages}
          onClose={() => setShowPreviewEditor(false)}
          onDownload={() => setShowPreviewEditor(false)}
        />
      )}
    </div>
  );
};

export default Analytics;
